---
title: Files
lede: The sandbox's files, in a code editor that saves into the session, with viewers for documents, tables and media.
order: 18
part: Run a task
---

The Files surface (`⌘2`) shows every repository of the session under one tree, as the agent sees it. Use it to read the agent's work or change something yourself.

![The file tree](media/files-tree.webp)

### The tree

A name in colour is a file this session changed. Hover it for the kind of change:

| Name | Meaning |
|---|---|
| Green | New file |
| Blue | Modified or renamed |
| Red, struck through | Deleted |
| Red | In conflict — see [Sync and conflicts](/sync) |

A folder holding changes shows in bold. Right-click a row, press `⇧F10`, or use the **…** that appears on it, for **Open in an app on this computer**, **New file**, **New folder**, **Rename**, **Move**, **Copy path** and **Delete**. Open tabs follow a file you rename or move. A repository's root folder keeps its name and place; it is removed in [workspace settings](/repositories), not here.

### Editing

![The editor](media/files-editor.webp)

The editor saves on its own a moment after you stop typing, into the session's copy only. Your own checkout changes only when you [merge](/merging).

Files are read-only while the agent is working or the sandbox is starting; the bar over the tree says which. What the agent writes appears in the open file as it lands.

Markdown files show their formatting as you edit. Select text in any file and press **Annotate** to leave a note for the agent, as in [Changes](/changes).

### Finding your way

| Keys | Opens |
|---|---|
| `⇧` twice, or `⌘P` | **Go to File…**: type part of a name. Start with `@` for a symbol of this file, `#` for a symbol anywhere, `:` for a line |
| `⌘T` | **Go to Symbol…** |
| `⌘E` | **Recent Files…** |
| `⌘⇧F` | **Find in Files…** |
| `⌘⇧H` | **Replace in Files…** |
| `⌘[` / `⌘]` | **Back** / **Forward** |

The same verbs are in the **Code** menu. `⌘F` finds in the open file.

**Find in Files** searches every repository of the session and skips what `.gitignore` leaves out. **Aa**, **W** and **.\*** at the end of the field match case, whole words and regular expressions; **Files to include** and **Files to exclude** narrow the search. A result opens its file at the match. To replace, tick the files and press **Replace all**.

### Language servers

A language server gives the editor what an IDE knows about the code. It runs inside the session, and the strip under the file names the server serving it.

| What | Keys |
|---|---|
| Go to definition | `F12`, or `⌘`-click a name |
| Go to implementation | `⌘F12` |
| Find references | `⇧F12` |
| Rename everywhere | `F2` |
| Quick fixes | `⌘.` |
| Next / previous error | `F8` / `⇧F8` |
| Parameter info | `⌘⇧Space` |
| File structure | `⌘⇧O` |

Hovering a name shows its type and documentation, completion follows what you type, and errors are underlined as you write.

A server the session does not have is offered with **Install** in the strip. Nothing is installed without you.

### Changes, history and tests

Marks in the margin show the lines that differ from the session's base: added, modified, or a wedge where lines were removed. Click one to see what the base had, then **Revert** to put it back (it can be undone) or **Close**.

Right-click a tab for:

| Item | Does |
|---|---|
| **Pin tab** | Keeps the tab at the front of the row. A pinned tab is never closed to make room, nor by the **Close tabs** items |
| **Split right** / **Move to the left** / **Unsplit** | Shows two files side by side |
| **Annotate** | Shows who last changed each line, beside the numbers. Again to hide it |
| **Run…** | Runs this file or its tests in a dock shell, with the command the repository's manifests give |
| **Show history** | The file's commits; open one to compare it |
| **Compare with…** / **Compare with clipboard** | Opens a comparison as a tab |
| **Copy path** | Copies the file's path |

A triangle beside a test in the margin runs that test alone, in a dock shell.

### Live probes

On a running project, press the margin beside a line to set a [live probe](/live-probes) there; hold `⌥` for a snapshot. The program never pauses. Press the mark again to read what it captured, or **Remove** it.

### Keys and preferences

**Settings › General › Code editor** sets the editor for every workspace:

| Setting | What it does | Default |
|---|---|---|
| **Keys** | **Standard**, **VS Code**, **IntelliJ** or **Vim**. VS Code's own chords include `⌘D` for the next occurrence, `⇧⌘K` to delete a line and `⌥↑`/`⌥↓` to move one; IntelliJ's replace the ones above, such as `⌘O` for Go to File, `⌘B` for definition and `⌥↩` for quick fixes | Standard |
| **Wrap long lines** | Off, a long line scrolls sideways | On |
| **Indent guides** | A faint rule down each level of indentation | On |
| **Show whitespace** | Spaces and tabs drawn as faint marks | Off |
| **Minimap** | The whole file, small, beside the scrollbar | Off |
| **Format on save** | Runs the project's own formatter when you press `⌘S` | Off |
| **Language servers** | Starts them by themselves | On |

The editor follows the repository's `.editorconfig`, or the indentation it finds in the file.

### Tabs

Each open file is a tab. The tab row wraps to two lines; past that, opening a file closes the unpinned one you used longest ago.

Drag a tab to reorder it, middle-click to close it, or right-click for **Close tabs to the left**, **Close tabs to the right** and **Close all tabs**.

### Documents

PDFs, slide decks (`.pptx`) and images open as documents, read-only.

A Word file (`.docx`) opens as its printed pages. Press **Edit** and type on the pages: the text changes in the file itself, while styles, images and layout stay as they are. A paragraph holding a field, a tracked change or a note reference stays locked, and the band counts them. The file saves a moment after you stop typing, or on `⌘S`; **Done** ends editing. While the agent works, the pages are read-only. An old `.doc` is not drawn.

A spreadsheet (`.xlsx`, `.xls`, `.ods`) or a CSV opens as a table. Click a column header to sort (`⇧` adds a column), or its funnel to filter by value; **Clear filters** shows every row again. **Header row** switches whether the first row names the columns. Select cells with the arrows and `⇧`, and `⌘C` copies them. A table is never written back. On a CSV, **Edit as text** opens it in the editor.

An HTML file opens on its source. **Source** and **Preview**, in the tab row, switch to the page it draws. The preview runs nothing, and shows only images and stylesheets from the session.

Videos and sounds play in the pane. A file over 24 MB is not previewed.

**Open in an app on this computer** (**Open in app** on a Word file) copies the file out and opens it in your usual app for that type. Each time that app saves, the file goes back into the session — held until the agent stops if it is working.
